import { EditorContent, useEditor } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { cln } from '../../lib/utils';
import { MenuBar } from './Menubar';

interface Props {
  label?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  className?: string;
  initialValue?: string;
  onChange: (props: { html: string; obj: Record<string, any>; text: string }) => void;
}

export const RichText = forwardRef<any, Props>(
  ({ label, disabled, required = false, error, onChange, className, initialValue }, ref) => {
    const { t } = useTranslation();
    const id = crypto.randomUUID();
    const editor = useEditor(
      {
        extensions: [StarterKit, Underline],
        injectCSS: true,
        content: initialValue,
        onBlur: ({ editor }) =>
          onChange({ html: editor.getHTML(), obj: editor.getJSON(), text: editor.getText() }),
      },
      [],
    );

    useEffect(() => {
      if (editor && initialValue) {
        editor?.commands.setContent(initialValue);
      }
    }, [initialValue, editor]);

    useImperativeHandle(ref, () => ({
      getEditorInstance() {
        return editor;
      },
    }));

    return (
      <span className={cln('richtext', error && 'invalid', className)} data-required={required}>
        {label && (
          <label className="text-label" htmlFor={id}>
            {t(label)}
          </label>
        )}
        <div className="rt-wrapper">
          <MenuBar editor={editor} />
          <EditorContent disabled={disabled} className="rt-editor" id={id} editor={editor} />
        </div>
        {error && <span className="text-label text-danger-500">{t(error)}</span>}
      </span>
    );
  },
);

RichText.displayName = 'RichText';
